import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, Capabilities } from '@/api/client'
import { useMe } from '@/features/auth/useAuth'
import { fmtDate } from '@/i18n'
import { pk, STATUS_TONE, StatusPill, usePT } from './PeoplePage'

interface ProjectCrew { workers: number; mayWork: number; firstAiders: number; people: { employeeId: string; name: string | null; crew: string | null; status: string; firstAider: boolean }[] }

/** On a project: who worked there in the last 30 days and whether they may. Status only, never the papers. */
export function ProjectCrewCompliance({ projectId }: { projectId: string }) {
  const t = usePT()
  const { data } = useQuery<ProjectCrew>({ queryKey: ['crew-compliance', projectId], queryFn: () => api.get<ProjectCrew>(`/api/projects/${projectId}/crew-compliance`) })
  if (!data || data.workers === 0) return null
  const bad = data.workers - data.mayWork
  return (
    <details className="rounded-xl ring-1 ring-neutral-200 dark:ring-neutral-800">
      <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-3 py-2 text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('crewTitle')}</span>
        <span className={bad > 0 ? 'font-semibold text-red-700 dark:text-red-300' : ''}>{t('crewSummary', { workers: data.workers, may: data.mayWork, fa: data.firstAiders })}</span>
      </summary>
      <ul className="divide-y divide-neutral-100 border-t border-neutral-100 text-sm dark:divide-neutral-800 dark:border-neutral-800">
        {data.people.map((p) => (
          <li key={p.employeeId} className="flex items-center justify-between gap-2 px-3 py-1.5">
            <span>{p.name ?? '—'} <span className="text-xs text-neutral-500">· {p.crew ?? '—'}{p.firstAider ? ` · ${t('firstAider')}` : ''}</span></span>
            <StatusPill t={t} status={p.status} />
          </li>
        ))}
      </ul>
    </details>
  )
}

interface Alerts {
  blockedOnSite: number; notCheckedOnSite: number; briefingDueOnSite: number
  expiring: { employeeId: string; name: string | null; kind: string; validTo: string }[]
  subsBlocked: { companyId: string; name: string | null; missing: string[] }[]
  crewsWithoutFirstAider: { crewId: string; crew: string | null }[]
}

/** True when the People alerts have anything to show (for the empty state on "My work"). */
export function usePeopleAlertCount() {
  const { data: me } = useMe()
  const can = me?.capabilities.includes(Capabilities.ViewPeople) ?? false
  const { data } = useQuery<Alerts>({ queryKey: ['people-alerts'], queryFn: () => api.get<Alerts>('/api/people/alerts'), enabled: can })
  if (!can || !data) return 0
  return (data.blockedOnSite ? 1 : 0) + (data.notCheckedOnSite ? 1 : 0) + (data.briefingDueOnSite ? 1 : 0) + data.subsBlocked.length + data.crewsWithoutFirstAider.length + data.expiring.length
}

/** For HR on "My work": who is on site without papers, what expires soon, which subs and crews are short. */
export function PeopleAlerts() {
  const t = usePT()
  const { data: me } = useMe()
  const can = me?.capabilities.includes(Capabilities.ViewPeople) ?? false
  const { data } = useQuery<Alerts>({ queryKey: ['people-alerts'], queryFn: () => api.get<Alerts>('/api/people/alerts'), enabled: can })
  if (!can || !data) return null
  const lines: { tone: string; text: string; label?: string }[] = [
    ...(data.blockedOnSite ? [{ tone: 'blocked', text: t('alBlocked', { n: data.blockedOnSite }) }] : []),
    ...(data.notCheckedOnSite ? [{ tone: 'not_checked', text: t('alNotChecked', { n: data.notCheckedOnSite }) }] : []),
    ...(data.briefingDueOnSite ? [{ tone: 'briefing_due', text: t('alBriefing', { n: data.briefingDueOnSite }) }] : []),
    ...data.subsBlocked.map((s) => ({ tone: 'blocked', text: t('alSub', { name: s.name ?? '—', missing: s.missing.map((m) => pk(t, `sk_${m}`)).join(', ') }) })),
    ...data.crewsWithoutFirstAider.map((c) => ({ tone: 'blocked', label: t('noFirstAider'), text: t('alCrew', { crew: c.crew ?? '—' }) })),
    ...data.expiring.map((e) => ({ tone: 'expiring', text: t('alExpiring', { name: e.name ?? '—', kind: pk(t, `wk_${e.kind}`), date: fmtDate(e.validTo) }) })),
  ]
  if (lines.length === 0) return null
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('alertsTitle')} ({lines.length})</h2>
      <ul className="divide-y divide-neutral-100 overflow-hidden rounded-2xl bg-white ring-1 ring-neutral-200 dark:divide-neutral-800 dark:bg-neutral-900 dark:ring-neutral-800">
        {lines.map((l, i) => (
          <li key={i}><Link to="/people" className="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
            {l.label ? <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[l.tone]}`}>{l.label}</span> : <StatusPill t={t} status={l.tone} />}<span className="text-sm">{l.text}</span></Link></li>
        ))}
      </ul>
    </section>
  )
}
